import { JobOfferEmbedListShowingResponseDto } from "@/lib/interfaces/embed";
import { Badge, Box, Button, Card, CardBody, Container, Flex, Heading, Image, Spinner, Text } from "@chakra-ui/react";
import { ArrowRight, Euro, MapPin } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import useApiRequest from "../../lib/hooks/useRequest";
import { JobImagePlaceholder } from "./JobImagePlaceholder";

/*
 * Custom CSS Classes:
 * • job-iframe-container
 * • job-iframe-loading
 * • job-iframe-spinner
 * • job-iframe-content
 * • job-iframe-grid
 * • job-iframe-card
 * • job-iframe-image-container
 * • job-iframe-image
 * • job-iframe-image-placeholder
 * • job-iframe-card-body
 * • job-iframe-header
 * • job-iframe-title
 * • job-iframe-badge
 * • job-iframe-details
 * • job-iframe-location
 * • job-iframe-location-text
 * • job-iframe-salary
 * • job-iframe-salary-text
 * • job-iframe-benefits
 * • job-iframe-benefits-title
 * • job-iframe-benefits-content
 * • job-iframe-cta-button
 * • job-iframe-empty-state
 * • job-iframe-empty-heading
 * • job-iframe-empty-text
 */

export const parseTimeModel = (timeModels: string[], maxLength: number = 1): string => {
  if (timeModels.length === 0) {
    return "";
  }

  // Helper function to translate individual time model
  const translateTimeModel = (timeModel: string): string => {
    switch (timeModel) {
      case "fullTime":
        return "Vollzeit";
      case "partTime":
        return "Teilzeit";
      case "miniJob":
        return "Mini-Job";
      case "student":
        return "Student";
      case "apprenticeship":
        return "Ausbildung";
      case "internship":
        return "Praktikum";
      default:
        return timeModel;
    }
  };

  // Take up to maxLength time models and translate them
  const timeModelsToShow = timeModels.slice(0, maxLength);
  const translatedModels = timeModelsToShow.map(translateTimeModel);

  let result = translatedModels.join(", ");

  // Add "..." if there are more time models than maxLength
  if (timeModels.length > maxLength) {
    result += ", ...";
  }

  return result;
};

const JobIframe = ({ id }: { id: string }) => {
  const { apiRequest } = useApiRequest();
  const [companyData, setCompanyData] = useState<JobOfferEmbedListShowingResponseDto>();
  const [isLoading, setIsLoading] = useState(true);

  // Read query parameters and merge with embed settings
  const mergedSettings = useMemo(() => {
    if (!companyData?.embedSettings) return null;

    const urlParams = new URLSearchParams(window.location.search);
    const querySettings = {
      color: urlParams.get("color"),
      showImage: urlParams.get("showImage"),
      customCss: urlParams.get("customCss"),
    };

    return {
      color: querySettings.color || companyData.embedSettings.color || "#3182ce",
      showImage:
        querySettings.showImage !== null && querySettings.showImage !== ""
          ? querySettings.showImage === "true"
          : (companyData.embedSettings.showImage ?? true),
      customCss: querySettings.customCss || companyData.embedSettings.customCss || "",
    };
  }, [companyData]);

  const getCompanyData = useCallback(async () => {
    setIsLoading(true);
    const res = await apiRequest<JobOfferEmbedListShowingResponseDto>(`embed/${id}/job-offers`, "GET", {
      withoutAuth: true,
    });
    if (res.data) {
      setCompanyData(res.data);
    }
    setIsLoading(false);
  }, [apiRequest, id]);

  useEffect(() => {
    getCompanyData();
  }, [getCompanyData]);

  // Auto-resize iframe using postMessage
  useEffect(() => {
    function sendHeight() {
      if (window.parent && window.parent !== window) {
        // Get the height of the entire document
        const height = Math.max(
          document.body.scrollHeight,
          document.body.offsetHeight,
          document.documentElement.clientHeight,
          document.documentElement.scrollHeight,
          document.documentElement.offsetHeight,
        );
        window.parent.postMessage(height, "*");
      }
    }

    // Send height when content is loaded
    if (companyData && !isLoading) {
      // Small delay to ensure DOM is fully rendered
      setTimeout(sendHeight, 100);
    }

    // Listen for messages from parent and respond with height
    window.addEventListener(
      "message",
      function (e: MessageEvent) {
        if (isNaN(e.data)) return;
        sendHeight();
      },
      false,
    );

    // Send height on window resize
    window.addEventListener("resize", sendHeight);

    return () => {
      window.removeEventListener("resize", sendHeight);
    };
  }, [companyData, isLoading]);

  // Inject combined CSS (dynamic properties + custom CSS)
  useEffect(() => {
    if (mergedSettings) {
      // Generate dynamic CSS for accent color and layout
      const dynamicCss = `
        .job-iframe-card {
          border-radius: 8px;
          height: 100%;
          display: flex;
          flex-direction: column;
        }
        
        .job-iframe-card-body {
          flex: 1;
          display: flex;
          flex-direction: column;
        }
        
        .job-iframe-image {
          border-top-left-radius: 8px;
          border-top-right-radius: 8px;
        }
        
        .job-iframe-cta-button {
          background-color: ${mergedSettings.color};
          border-radius: 8px;
        }
        
        .job-iframe-cta-button:hover {
          background-color: ${mergedSettings.color};
          opacity: 0.9;
        }
        
        .job-iframe-badge {
          background-color: ${mergedSettings.color};
        }
        
        .job-iframe-image-placeholder {
          border-top-left-radius: 8px;
          border-top-right-radius: 8px;
        }
        
        /* Additional dynamic styling for consistency */
        .job-iframe-image-container {
          border-top-left-radius: 8px;
          border-top-right-radius: 8px;
          overflow: hidden;
        }
      `;

      // Combine dynamic CSS with custom CSS
      const combinedCss = dynamicCss + (mergedSettings.customCss || "");

      const styleElement = document.createElement("style");
      styleElement.textContent = combinedCss;
      styleElement.id = "job-iframe-combined-css";

      // Remove existing CSS
      const existingStyle = document.getElementById("job-iframe-combined-css");
      if (existingStyle) {
        existingStyle.remove();
      }

      document.head.appendChild(styleElement);

      return () => {
        styleElement.remove();
      };
    }
  }, [mergedSettings]);

  const handleJobClick = (jobId: string) => {
    window.open(`/job-angebote/iframe/${id}/${jobId}`, "_blank");
  };

  if (isLoading || !companyData || !mergedSettings) {
    return (
      <Flex className="justify-center w-full h-screen items-center job-iframe-loading">
        <Spinner size="xl" color="primary.500" className="job-iframe-spinner" />
      </Flex>
    );
  }

  return (
    <Box className="min-h-screen job-iframe-container">
      {/* Content */}
      <Container maxW="container.xl" className="py-8 job-iframe-content">
        {/* Job Cards Grid */}
        <Box
          display="grid"
          gridTemplateColumns={{ base: "1fr", md: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }}
          gap={6}
          className="job-iframe-grid"
        >
          {companyData.jobOffers.map((job, index) => (
            <Card
              key={job._id || index}
              className="shadow-lg hover:shadow-xl transition-shadow duration-300 cursor-pointer job-iframe-card"
              _hover={{ transform: "translateY(-2px)" }}
              transition="all 0.3s ease"
            >
              {/* Job Image */}
              {mergedSettings.showImage !== false && job.showImage !== false && (
                <Box className="w-full h-48 overflow-hidden job-iframe-image-container">
                  {job.imageUrl ? (
                    <Image src={job.imageUrl} alt={job.title} className="w-full h-full object-cover job-iframe-image" />
                  ) : (
                    <JobImagePlaceholder
                      title={job.title}
                      color={mergedSettings.color}
                      className="job-iframe-image-placeholder"
                    />
                  )}
                </Box>
              )}

              <CardBody className="p-6 job-iframe-card-body flex flex-col h-full">
                {/* Content Container - grows to fill available space */}
                <Box flex="1">
                  {/* Job Title and Badge */}
                  <Flex justify="space-between" align="start" mb={4} className="job-iframe-header">
                    <Heading
                      size="md"
                      color="gray.800"
                      flex="1"
                      mr={2}
                      className="job-iframe-title line-clamp-2 overflow-hidden text-ellipsis"
                    >
                      {job.title}
                    </Heading>
                    <Badge
                      fontSize="sm"
                      px={2}
                      py={1}
                      borderRadius="full"
                      flexShrink={0}
                      color="white"
                      className="job-iframe-badge"
                    >
                      {parseTimeModel(job.belongsToWorkingTimeModels, 1)}
                    </Badge>
                  </Flex>

                  {/* Job Details */}
                  <Flex gap={4} wrap="wrap" className="text-gray-600 mb-4 job-iframe-details">
                    {job.postCode && (
                      <Flex align="center" gap={2} className="job-iframe-location">
                        <MapPin size={16} />
                        <Text fontSize="sm" fontWeight="medium" className="job-iframe-location-text">
                          {job.postCode}
                        </Text>
                      </Flex>
                    )}
                    <Flex align="center" gap={2} className="job-iframe-salary">
                      {job.hourlySalary && <Euro size={16} />}

                      <Text
                        fontSize="sm"
                        fontWeight="medium"
                        className={`job-iframe-salary-text ${job.hourlySalary ? "" : "invisible"}`}
                      >
                        {job.hourlySalary ? `${job.hourlySalary}€/Stunde` : "-"}
                      </Text>
                    </Flex>
                  </Flex>

                  {/* Benefits Preview */}
                  <Box mb={4} className="job-iframe-benefits">
                    <Text
                      fontSize="sm"
                      fontWeight="semibold"
                      color="gray.700"
                      mb={2}
                      className="job-iframe-benefits-title"
                    >
                      Beschreibung:
                    </Text>
                    <Box
                      className="job-iframe-benefits-content text-sm text-gray-600 line-clamp-3 overflow-hidden text-ellipsis"
                      dangerouslySetInnerHTML={{ __html: job.description }}
                    />
                  </Box>
                </Box>

                {/* CTA Button - always at bottom */}
                <Box mt="auto" pt={4}>
                  <Button
                    size="md"
                    width="full"
                    rightIcon={<ArrowRight size={16} />}
                    color="white"
                    className="job-iframe-cta-button"
                    onClick={() => handleJobClick(job._id || `job${index + 1}`)}
                  >
                    Mehr anzeigen
                  </Button>
                </Box>
              </CardBody>
            </Card>
          ))}
        </Box>

        {/* Empty State */}
        {companyData.jobOffers.length === 0 && (
          <Box className="text-center py-16 job-iframe-empty-state">
            <Heading size="lg" color="gray.600" mb={4} className="job-iframe-empty-heading">
              Keine offenen Stellen verfügbar
            </Heading>
            <Text color="gray.500" className="job-iframe-empty-text">
              Derzeit sind keine Stellenausschreibungen verfügbar. Schauen Sie gerne später wieder vorbei.
            </Text>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export { JobIframe };
